import { useEffect, useRef, useState } from 'react';
import { Datagrid } from 'datagrid';
import type { TableCellRenderers, TablePresentationOptions } from 'datagrid';
import { SiteNav } from '../components/SiteNav';
import { SiteFooter } from '../components/SiteFooter';
import { orderMailto } from '../lib/orderMailto';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'Demo',     href: '#demo' },
  { label: 'Pricing',  href: '#pricing' },
  { label: 'Docs',     href: 'https://github.com/vibelibs/datasynth#readme' },
];

const footerLinks = [
  { label: 'GitHub', href: 'https://github.com/vibelibs/datasynth' },
  { label: 'Benchmarks', href: 'https://github.com/vibelibs/datasynth/blob/main/BENCHMARKS.md' },
  { label: 'Docs', href: 'https://github.com/vibelibs/datasynth#readme' },
  { label: 'Contact', href: 'mailto:hello@vibelibs.dev' },
];

const runCmd = 'npx datasynth-server';

const features = [
  {
    icon: '⚙',
    color: 'green' as const,
    name: 'Seedable generation engine',
    desc: 'Faker-backed synthetic rows with a deterministic seed. Same seed, same dataset — reproducible fixtures for tests and demos.',
  },
  {
    icon: '✓',
    color: 'green' as const,
    name: 'Quality checks built in',
    desc: 'Completeness, uniqueness, range validity, and referential integrity, each scored 0–1. Generation and governance in one engine.',
  },
  {
    icon: '⚕',
    color: 'purple' as const,
    name: 'OMOP & common data models',
    desc: 'Generate multi-table clinical CDM datasets where one "person" expands into related records across tables — not just flat rows.',
  },
  {
    icon: '◳',
    color: 'amber' as const,
    name: 'Noise injection',
    desc: 'Realistic dirty-data and enhanced-noise modes. Stress-test your pipelines against the messy data they will actually meet.',
  },
  {
    icon: '⤓',
    color: 'purple' as const,
    name: 'Export anywhere',
    desc: 'Download datasets as SQLite or CSV. Export quality reports as PDF or CSV for auditors. No proprietary formats.',
  },
  {
    icon: '⛁',
    color: 'green' as const,
    name: 'Self-hosted & stateless',
    desc: 'A single Node process, state in memory and on disk under your working dir. Runs entirely in your own infrastructure.',
  },
  {
    icon: '⦸',
    color: 'red' as const,
    name: 'Offline by default',
    desc: 'External reference connectors are disabled and fall back to bundled mock data. No surprise network calls on a generate.',
  },
  {
    icon: '⚷',
    color: 'amber' as const,
    name: 'API-key + session auth',
    desc: 'Browsers exchange the key for an HttpOnly, SameSite=Strict cookie. Server-to-server clients use x-api-key. The browser never holds the raw key.',
  },
  {
    icon: '⧗',
    color: 'purple' as const,
    name: 'TTL eviction',
    desc: 'Generated datasets auto-expire after a configurable TTL and return a clean 410. Predictable, bounded memory footprint.',
  },
  {
    icon: '⬡',
    color: 'green' as const,
    name: 'Zero platform coupling',
    desc: 'No Prisma, no dataserver dependency. Three npm tarballs — install them into your own project or run the bundled server bin.',
  },
  {
    icon: '⌨',
    color: 'amber' as const,
    name: 'npx-ready server',
    desc: 'The datasynth-server bin gets you from nothing to a running, authenticated API in a single command. No build step required.',
  },
  {
    icon: '◷',
    color: 'purple' as const,
    name: 'Ships the datagrid',
    desc: 'Generated rows render in @vibelibs/datagrid out of the box — sort, filter, and inspect synthetic data with the same UI we sell.',
  },
];

// ── Faux demo data (pre-baked — no backend) ──────────────
type SynthRow = {
  rowid: number;
  person_id: string;
  gender: string;
  birth_year: number;
  race: string;
  condition: string;
  valid: 'Pass' | 'Warn';
};

const synthColumns = ['person_id', 'gender', 'birth_year', 'race', 'condition', 'valid'];

const synthRows: SynthRow[] = [
  { rowid: 1, person_id: 'P-100423', gender: 'Female', birth_year: 1984, race: 'White',            condition: 'Type 2 Diabetes',  valid: 'Pass' },
  { rowid: 2, person_id: 'P-100424', gender: 'Male',   birth_year: 1971, race: 'Black',            condition: 'Hypertension',     valid: 'Pass' },
  { rowid: 3, person_id: 'P-100425', gender: 'Female', birth_year: 1996, race: 'Asian',            condition: 'Asthma',           valid: 'Warn' },
  { rowid: 4, person_id: 'P-100426', gender: 'Male',   birth_year: 1959, race: 'White',            condition: 'Atrial Fib.',      valid: 'Pass' },
  { rowid: 5, person_id: 'P-100427', gender: 'Female', birth_year: 2002, race: 'Hispanic',         condition: 'Migraine',         valid: 'Pass' },
  { rowid: 6, person_id: 'P-100428', gender: 'Male',   birth_year: 1988, race: 'Native American',  condition: 'Hyperlipidemia',   valid: 'Pass' },
];

const synthCellRenderers: TableCellRenderers = {
  person_id: ({ value }) => <span className="dg-live-mono">{String(value)}</span>,
  birth_year: ({ value }) => <span className="dg-live-mono">{String(value)}</span>,
  condition: ({ value }) => <span className="dg-live-name">{String(value)}</span>,
  valid: ({ value }) => (
    <span className={`dg-badge ${value === 'Pass' ? 'dg-badge-green' : 'dg-badge-amber'}`}>
      {String(value)}
    </span>
  ),
};

const synthPresentation: TablePresentationOptions = {
  showHeaderInfoBar: false,
  showFooter: false,
  enableHorizontalScroll: false,
  rowHeight: 36,
  columnWidths: {
    person_id: 96,
    gender: 78,
    birth_year: 80,
    race: 116,
    condition: 130,
    valid: 70,
  },
  headerDisplay: {
    showColumnMenu: false,
    showSortControls: true,
    sortControlVisibility: 'always',
    showFilterControls: false,
    showSamplingControls: false,
    showStatisticsControls: false,
    showDatatypeIcons: false,
    showMissingDataIndicator: false,
  },
  typography: {
    table: {
      fontFamily: 'Space Grotesk, ui-sans-serif, system-ui, -apple-system, sans-serif',
      letterSpacing: '0',
    },
    header: {
      fontSize: '0.72rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--faint)',
    },
    body: {
      fontSize: '0.8rem',
      lineHeight: 1.25,
      fontWeight: 400,
      letterSpacing: '0',
      color: 'var(--muted)',
    },
  },
  columnLabels: {
    person_id: 'Person',
    gender: 'Gender',
    birth_year: 'Born',
    race: 'Race',
    condition: 'Condition',
    valid: 'Valid',
  },
  cellRenderers: synthCellRenderers,
  cellText: {
    person_id: { mode: 'truncate', maxLines: 1 },
    gender: { mode: 'truncate', maxLines: 1 },
    birth_year: { mode: 'truncate', maxLines: 1 },
    race: { mode: 'truncate', maxLines: 1 },
    condition: { mode: 'truncate', maxLines: 1 },
    valid: { mode: 'truncate', maxLines: 1 },
  },
};

type Tone = 'green' | 'amber';
const qualityScores: { label: string; score: number; tone: Tone }[] = [
  { label: 'Completeness',  score: 0.99, tone: 'green' },
  { label: 'Uniqueness',    score: 1.0,  tone: 'green' },
  { label: 'Range validity', score: 0.94, tone: 'amber' },
  { label: 'Referential',   score: 0.97, tone: 'green' },
];

type DemoPhase = 'idle' | 'generating' | 'scoring' | 'done';

const phaseLabel: Record<DemoPhase, string> = {
  idle: 'Ready · POST /v1/synthetic-data/generate',
  generating: 'Generating 5,000 OMOP persons…',
  scoring: 'Running quality checks…',
  done: 'Done · overall_score 0.96 · passed',
};

function DatasynthDemo() {
  const [phase, setPhase] = useState<DemoPhase>('idle');
  const timers = useRef<number[]>([]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  function run() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase('generating');
    timers.current.push(window.setTimeout(() => setPhase('scoring'), 1100));
    timers.current.push(window.setTimeout(() => setPhase('done'), 2000));
  }

  const busy = phase === 'generating' || phase === 'scoring';
  const showResults = phase === 'done';

  return (
    <div className="datagrid-preview" aria-label="datasynth demo" id="demo">
      <div className="dg-toolbar">
        <span className="dg-dot dg-dot-r" />
        <span className="dg-dot dg-dot-y" />
        <span className="dg-dot dg-dot-g" />
        <span style={{ marginLeft: '8px', fontSize: '0.72rem', fontFamily: 'JetBrains Mono, monospace', color: 'var(--faint)' }}>
          datasynth · OMOP
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--faint)' }}>:8088</span>
      </div>
      <div className="dg-tab-row">
        <span className={`dg-tab${!showResults ? ' is-active' : ''}`}>Generate</span>
        <span className={`dg-tab${showResults ? ' is-active' : ''}`}>Quality</span>
        <span className="dg-tab">Download</span>
      </div>

      <div className="ds-demo-controls">
        <button type="button" className="ds-gen-btn" onClick={run} disabled={busy}>
          {busy ? (
            <><span className="ds-spinner" /> Working…</>
          ) : showResults ? (
            '↻ Generate again'
          ) : (
            '▶ Generate 5,000 patients · OMOP'
          )}
        </button>
        <div className={`ds-status${busy ? ' is-busy' : ''}${showResults ? ' is-done' : ''}`}>
          {phaseLabel[phase]}
        </div>
        <div className="ds-progress" aria-hidden="true">
          <span
            className="ds-progress-fill"
            style={{ width: phase === 'idle' ? '0%' : phase === 'generating' ? '45%' : phase === 'scoring' ? '80%' : '100%' }}
          />
        </div>
      </div>

      {showResults && (
        <>
          <div className="ds-score-row">
            <div className="ds-score-card overall">
              <span className="ds-score-label">Overall</span>
              <span className="ds-score-value">96%</span>
              <span className="dg-badge dg-badge-green" style={{ marginTop: '2px' }}>passed</span>
            </div>
            {qualityScores.map((q) => (
              <div key={q.label} className="ds-score-card">
                <span className="ds-score-label">{q.label}</span>
                <span className={`ds-score-value ${q.tone}`}>{Math.round(q.score * 100)}%</span>
              </div>
            ))}
          </div>
          <div className="dg-live-grid">
            <Datagrid
              idField="rowid"
              columns={synthColumns}
              data={synthRows}
              page={1}
              pageSize={synthRows.length}
              pageSizeOptions={[synthRows.length]}
              totalRows={5000}
              totalPages={1}
              layoutOptions={{ heightMode: 'content' }}
              presentation={synthPresentation}
            />
          </div>
          <div className="ds-demo-foot">Preview of 6 of 5,000 generated rows · download as SQLite or CSV</div>
        </>
      )}
    </div>
  );
}

const faqItems = [
  {
    q: 'Does it need a database, Prisma, or our platform?',
    a: 'No. datasynth is fully standalone — three npm packages with <strong>no Prisma and no platform coupling</strong>. Generated datasets and run history live in memory and on disk under a working directory you control. A <code>guard:deps</code> check fails the build if any source ever imports a platform dependency.',
  },
  {
    q: 'Can it generate OMOP / clinical common-data-model datasets?',
    a: 'Yes. Pick a CDM model and one generated "person" expands into related records across multiple tables, the way real clinical data is shaped. CDM rows are capped separately from flat rows because each one fans out, so you control the blast radius.',
  },
  {
    q: 'Is any of my data sent to a third party?',
    a: 'No. datasynth is <strong>offline by default</strong> — external reference-data connectors are disabled and fall back to bundled mock data. A generate request that carries external sources is noted in the response summary and ignored; it never becomes a network call.',
  },
  {
    q: 'How large a dataset can one request generate?',
    a: 'Generation runs synchronously inside the request, so there is a hard row cap (<code>SYNTHETIC_MAX_SYNC_ROWS</code>, default 100k). The engine is CPU and event-loop bound — run the included benchmark on your target hardware and tune the cap down from the observed memory and latency ceiling. See <code>BENCHMARKS.md</code>.',
  },
  {
    q: 'How does authentication work?',
    a: 'The browser calls <code>/login</code> and exchanges an API key for an HttpOnly, SameSite=Strict session cookie — the raw key never reaches client JavaScript. Server-to-server clients send <code>x-api-key</code> directly. Terminate TLS at your reverse proxy.',
  },
  {
    q: 'What can I export?',
    a: 'Generated datasets download as <strong>SQLite</strong> or <strong>CSV</strong>. Quality-check results export as <strong>PDF</strong> (for auditors) or <strong>CSV</strong> (for pipelines). The success envelope is always <code>{ status: "success", data }</code>.',
  },
  {
    q: 'Can I run it across multiple replicas?',
    a: 'No — that is intentional. datasynth is single-process and stateless by design: restart drops all state, and generated datasets TTL-evict on a timer. If you need horizontal scale, that is a different product; this one is built to drop into one box in your own infra and stay simple.',
  },
  {
    q: 'How is it delivered and licensed?',
    a: 'You receive versioned npm tarballs — core, server, and UI — validated to install and run outside our monorepo by a clean-room script. <code>npm install</code> them into your own project or run <code>npx datasynth-server</code>. A paid license covers production use in your own infrastructure.',
  },
];

export function DatasynthPage() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = '@vibelibs/datasynth — Synthetic Data + Quality Checks, Self-Hosted';
    return () => { document.title = prev; };
  }, []);

  function copyRun() {
    void navigator.clipboard.writeText(runCmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div className="site">
      <SiteNav product="datasynth" links={navLinks} ctaHref="#pricing" ctaLabel="Get a license" />

      <main>
        {/* ── Hero ──────────────────────────────────────── */}
        <section className="hero" id="home">
          <div className="wrap">
            <div className="hero-grid">
              <div>
                <div className="hero-eyebrow">
                  <span className="hero-eyebrow-dot" />
                  @vibelibs/datasynth v0.1
                </div>
                <h1 className="hero-title">
                  Synthetic data your <em>auditors trust.</em>
                </h1>
                <p className="hero-lead">
                  Generate realistic, seedable datasets — flat tables or full OMOP clinical
                  models — and score their quality in the same engine. Self-hosted, offline by
                  default, zero platform coupling. One command to a running, authenticated API.
                </p>

                <div className="hero-install">
                  <span className="hero-install-prompt">$</span>
                  <span>{runCmd}</span>
                  <button
                    type="button"
                    className={`hero-install-copy${copied ? ' copied-text' : ''}`}
                    onClick={copyRun}
                    aria-label="Copy run command"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <div className="hero-actions" style={{ marginTop: '32px' }}>
                  <a className="btn-primary" href="#demo">See it generate</a>
                  <a
                    className="btn-ghost"
                    href="https://github.com/vibelibs/datasynth"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub ↗
                  </a>
                </div>
              </div>

              {/* ── Live faux demo ──────────────────────── */}
              <DatasynthDemo />
            </div>
          </div>
        </section>

        {/* ── Stats bar ─────────────────────────────────── */}
        <div className="wrap">
          <div className="stats-row">
            <div className="stat-cell">
              <div className="stat-number">3</div>
              <div className="stat-label">npm packages</div>
            </div>
            <div className="stat-cell">
              <div className="stat-number">0</div>
              <div className="stat-label">platform deps</div>
            </div>
            <div className="stat-cell">
              <div className="stat-number">OMOP</div>
              <div className="stat-label">+ CDM support</div>
            </div>
            <div className="stat-cell">
              <div className="stat-number">100%</div>
              <div className="stat-label">offline by default</div>
            </div>
          </div>
        </div>

        {/* ── Features ──────────────────────────────────── */}
        <section className="section" id="features">
          <div className="wrap">
            <p className="section-label">What you get</p>
            <h2 className="section-title">A generator and a governance engine in one box.</h2>
            <p className="section-sub" style={{ marginBottom: '48px' }}>
              Stop gluing a faker script to a separate data-quality tool to a separate exporter.
              datasynth generates the data, scores it, and hands you the files — behind one
              authenticated API you run yourself.
            </p>
            <div className="features-grid">
              {features.map((f) => (
                <article key={f.name} className="feature-cell">
                  <div className={`feature-icon ${f.color}`}>{f.icon}</div>
                  <div className="feature-name">{f.name}</div>
                  <div className="feature-desc">{f.desc}</div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Code example ──────────────────────────────── */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <p className="section-label">Drop-in ready</p>
            <h2 className="section-title">Generate, score, download — three calls.</h2>
            <p className="section-sub" style={{ marginBottom: '48px' }}>
              A small, predictable HTTP surface under API-key or session auth. Every success
              returns <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem', color: 'var(--green)', background: 'var(--surface-3)', padding: '1px 5px', borderRadius: '4px' }}>{'{ status, data }'}</code>.
            </p>
            <div className="code-layout">
              <div>
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="dg-dot dg-dot-r" />
                    <span className="dg-dot dg-dot-y" />
                    <span className="dg-dot dg-dot-g" />
                    <span className="code-block-filename" style={{ marginLeft: '6px' }}>generate.sh</span>
                    <span className="code-block-lang">bash</span>
                  </div>
                  <pre>{
`<span class="cm"># 1 — exchange API key for a session cookie</span>
<span class="fn">curl</span> -X POST <span class="str">:8088/login</span> \\
  -H <span class="str">'content-type: application/json'</span> \\
  -d <span class="str">'{"apiKey":"\$DATASYNTH_API_KEY"}'</span> -c jar

<span class="cm"># 2 — generate a 5,000-person OMOP dataset</span>
<span class="fn">curl</span> -X POST <span class="str">:8088/v1/synthetic-data/generate</span> \\
  -b jar -H <span class="str">'content-type: application/json'</span> \\
  -d <span class="str">'{"model":"patient","commonDataModel":"OMOP","rows":5000}'</span>

<span class="cm"># 3 — download it as SQLite</span>
<span class="fn">curl</span> <span class="str">:8088/v1/synthetic-data/\$ID/download?format=sqlite</span> \\
  -b jar -o dataset.sqlite`
                  }</pre>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="dg-dot dg-dot-r" />
                    <span className="dg-dot dg-dot-y" />
                    <span className="dg-dot dg-dot-g" />
                    <span className="code-block-filename" style={{ marginLeft: '6px' }}>quality.sh</span>
                    <span className="code-block-lang">bash</span>
                  </div>
                  <pre>{
`<span class="cm"># run quality checks on a generated db</span>
<span class="fn">curl</span> -X POST \\
  <span class="str">:8088/v1/databases/\$ID/quality-checks/run</span> \\
  -b jar

<span class="cm"># overall_score is 0–1, status passed|failed</span>
<span class="cm"># export the report for auditors</span>
<span class="fn">curl</span> <span class="str">:8088/v1/databases/\$ID/\\
  quality-checks/export?format=pdf</span> \\
  -b jar -o report.pdf`
                  }</pre>
                </div>
                <div className="showcase-mini-grid">
                  <div className="showcase-mini-cell">
                    <strong>Stateless</strong>
                    <span>One process, in-memory</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>SQLite · CSV</strong>
                    <span>Open export formats</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>Node 20+</strong>
                    <span>npx, no build step</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Showcase ──────────────────────────────────── */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <p className="section-label">Deep dives</p>
            <h2 className="section-title">Three things most faker scripts can't do.</h2>
            <p className="section-sub" style={{ marginBottom: '48px' }}>
              Generating random rows is easy. Generating <em>governed</em>, model-shaped,
              auditable data in your own infra is the part that usually takes a quarter.
            </p>
            <div className="showcase-grid">
              <div className="showcase-card featured">
                <div style={{ flex: 1 }}>
                  <span className="showcase-badge">✓ Quality checks</span>
                  <h3 className="showcase-title" style={{ marginTop: '16px' }}>
                    Generation and governance, one engine.
                  </h3>
                  <p className="showcase-body">
                    Most synthetic-data tools stop at "here are some rows." datasynth scores what
                    it generates: completeness, uniqueness, range validity, and referential
                    integrity, each as a <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem', color: 'var(--green)', background: 'var(--surface-3)', padding: '1px 5px', borderRadius: '4px' }}>0–1</code> score
                    with a <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem', color: 'var(--green)', background: 'var(--surface-3)', padding: '1px 5px', borderRadius: '4px' }}>passed | failed</code> verdict.
                  </p>
                  <p className="showcase-body" style={{ marginTop: '12px' }}>
                    The same quality registry runs in our platform — vendored in, drift-guarded by
                    a test, so your standalone copy never silently diverges. Export the run as a
                    PDF and hand it to an auditor.
                  </p>
                </div>
                <div style={{ flexShrink: 0, width: '280px' }}>
                  <div className="code-block">
                    <div className="code-block-header">
                      <span className="hero-eyebrow-dot" style={{ width: '7px', height: '7px' }} />
                      <span className="code-block-filename">run record</span>
                    </div>
                    <pre style={{ fontSize: '0.78rem' }}>{
`<span class="ink">{</span>
  <span class="attr">"status"</span><span class="ink">:</span> <span class="str">"passed"</span><span class="ink">,</span>
  <span class="attr">"overall_score"</span><span class="ink">:</span> <span class="val">0.96</span><span class="ink">,</span>
  <span class="attr">"issues_found"</span><span class="ink">:</span> <span class="val">12</span><span class="ink">,</span>
  <span class="attr">"check_type"</span><span class="ink">:</span> <span class="str">"range"</span>
<span class="ink">}</span>`
                    }</pre>
                  </div>
                </div>
              </div>

              <div className="showcase-card">
                <span className="showcase-badge purple">⚕ OMOP / CDM</span>
                <h3 className="showcase-title">Model-shaped, not just flat.</h3>
                <p className="showcase-body">
                  Choose a common data model and one synthetic "person" fans out into condition,
                  observation, and visit records that actually reference each other. The output is
                  shaped like real clinical data, so it exercises real joins.
                </p>
                <div className="showcase-mini-grid" style={{ marginTop: 'auto' }}>
                  <div className="showcase-mini-cell">
                    <strong>Persons</strong>
                    <span>Root entities</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>Fan-out</strong>
                    <span>Related tables</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>Cap</strong>
                    <span>Bounded rows</span>
                  </div>
                </div>
              </div>

              <div className="showcase-card">
                <span className="showcase-badge purple">⛁ Your infra</span>
                <h3 className="showcase-title">Runs where your data has to stay.</h3>
                <p className="showcase-body">
                  Single process, offline by default, no Prisma and no platform dependency. Hand
                  the tarballs to a regulated client and they run it on their own box — no data
                  leaves, no third party in the loop, no surprise network calls on a generate.
                </p>
                <div className="showcase-mini-grid" style={{ marginTop: 'auto' }}>
                  <div className="showcase-mini-cell">
                    <strong>Offline</strong>
                    <span>Mock fallback</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>TTL</strong>
                    <span>Auto-evict</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>Auth</strong>
                    <span>Key + session</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Pricing ───────────────────────────────────── */}
        <section className="section" id="pricing">
          <div className="wrap">
            <p className="section-label">Pricing</p>
            <h2 className="section-title">Run it yourself. Pay once, or by the year.</h2>
            <p className="section-sub" style={{ marginBottom: '56px' }}>
              datasynth is delivered as tarballs you host in your own infrastructure. Evaluate for
              free, buy a perpetual self-host license for production, or take Enterprise for
              multi-environment use, support, and custom models.
            </p>
            <div className="pricing-grid">
              {/* Evaluation */}
              <div className="pricing-card">
                <div className="pricing-tier">Evaluation</div>
                <div className="pricing-price">
                  <span className="pricing-currency" style={{ fontSize: '1.1rem', alignSelf: 'flex-start', marginTop: '6px' }}></span>
                  <span className="pricing-amount">Free</span>
                  <span className="pricing-period">non-production</span>
                </div>
                <div className="pricing-desc">
                  Run the full engine locally to evaluate. Not licensed for production use.
                </div>
                <div className="pricing-divider" />
                <ul className="pricing-features">
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Full generation + quality engine</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>OMOP / CDM models</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>SQLite / CSV / PDF export</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Community GitHub issues</li>
                  <li className="pricing-feature"><span className="pricing-check muted">—</span><span style={{ color: 'var(--faint)' }}>Production license</span></li>
                  <li className="pricing-feature"><span className="pricing-check muted">—</span><span style={{ color: 'var(--faint)' }}>Priority support</span></li>
                </ul>
                <a className="pricing-btn" href="https://github.com/vibelibs/datasynth" target="_blank" rel="noopener noreferrer">
                  Get on GitHub
                </a>
              </div>

              {/* Self-Hosted — featured */}
              <div className="pricing-card featured">
                <div className="pricing-featured-tag">For product teams</div>
                <div className="pricing-tier">Self-Hosted</div>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">299</span>
                  <span className="pricing-period">one-time</span>
                </div>
                <div className="pricing-desc">
                  Perpetual production license for one server, in your own infrastructure.
                </div>
                <div className="pricing-divider" />
                <ul className="pricing-features">
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Everything in Evaluation</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Production use, unlimited datasets</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Bundled <code style={{ fontSize: '0.8rem' }}>@vibelibs/datagrid</code> UI</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Versioned tarballs + clean-room check</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>One year of updates</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Email support · 48h SLA</li>
                </ul>
                <a className="pricing-btn primary" href={orderMailto({ product: '@vibelibs/datasynth', tier: 'Self-Hosted', price: '$299 one-time' })}>
                  Buy Self-Hosted
                </a>
              </div>

              {/* Enterprise */}
              <div className="pricing-card">
                <div className="pricing-tier">Enterprise</div>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">1,990</span>
                  <span className="pricing-period">/year</span>
                </div>
                <div className="pricing-desc">
                  For regulated teams running datasynth across multiple environments.
                </div>
                <div className="pricing-divider" />
                <ul className="pricing-features">
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Everything in Self-Hosted</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Unlimited servers + environments</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Custom models + quality checks</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Air-gapped deployment support</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Custom SLA + dedicated support</li>
                  <li className="pricing-feature"><span className="pricing-check">✓</span>Invoice billing available</li>
                </ul>
                <a className="pricing-btn" href={orderMailto({ product: '@vibelibs/datasynth', tier: 'Enterprise', price: '$1,990/year' })}>
                  Contact us
                </a>
              </div>
            </div>

            <p style={{ textAlign: 'center', marginTop: '32px', fontSize: '0.85rem', color: 'var(--muted)' }}>
              Need it air-gapped or on specific hardware?{' '}
              <a href="mailto:hello@vibelibs.dev" style={{ color: 'var(--green)' }}>Write us</a>
              {' '}and we'll size it with you.
            </p>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────── */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <p className="section-label">FAQ</p>
            <h2 className="section-title">Questions we get.</h2>
            <div className="faq-grid" style={{ marginTop: '40px' }}>
              {faqItems.map((item) => (
                <div key={item.q} className="faq-item">
                  <div className="faq-q">{item.q}</div>
                  <div className="faq-a" dangerouslySetInnerHTML={{ __html: item.a }} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Banner CTA ────────────────────────────────── */}
        <div className="wrap">
          <div className="banner">
            <h2>Ship realistic test data without shipping real data.</h2>
            <p>
              Your team needs production-shaped datasets to build and test against. Your
              compliance team needs them to never contain a real person. datasynth is how you
              give both groups what they want.
            </p>
            <div className="banner-actions">
              <a className="btn-primary" href="#pricing">Get a license →</a>
              <a
                className="btn-ghost"
                href="https://github.com/vibelibs/datasynth"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read the source
              </a>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter links={footerLinks} />
    </div>
  );
}
